/**
 * WebSocket relay client for the Worker `/v1/ws` endpoint (task-9 skeleton).
 *
 * Transport contract (see apps/worker/src/relay/frames.ts and
 * docs/PROTOCOL-v0.md §9):
 * - client → worker: `ping`, `ack {seq}`, `relay {to, envelope}`, `subscribe {topics}`
 * - worker → client: `hello`, `message {seq, envelope}`, `pong`, `ack`, `delivery`, `error`
 *
 * The Worker only sees opaque envelopes. This client adds, on top of the
 * original minimal relay loop:
 * - injectable WebSocket factory + timers/random (fully offline unit tests);
 * - exponential-backoff reconnect with deterministic scheduling;
 * - auth-failure mapping (close codes / AUTH_* error frames → fatal, no retry);
 * - E2E envelope send/receive via the already-derived channel key (fail-closed:
 *   plaintext or undecryptable envelopes are never dispatched as events);
 * - inbound `seq` gap detection → no ack beyond the gap → resync by reconnecting
 *   (the Worker replays every unacknowledged queue entry on each connection);
 * - dedupe by envelope id and seq, bounded offline outbox replayed in order;
 * - decoded event dispatch (`onEvent`) for `chat.delta` / `approval.*` / `event` …
 *
 * Live mode in AppState keeps using the same class without the new options, so
 * its behaviour is unchanged (raw `onMessage` + ack). This file never opens a
 * socket on its own; tests inject a fake WebSocket.
 */
import type { Envelope, ErrorCode, MessageType } from '@luminis/protocol';
import { decryptEnvelope, encryptEnvelope } from '../crypto/e2e';
import { nowSeconds } from '../id';
import {
  DEFAULT_RECONNECT_POLICY,
  canReconnect,
  reconnectDelay,
  type ReconnectPolicy,
} from '../live/reconnect';

export type RelayRole = 'phone' | 'daemon';
export type RelayStatus = 'idle' | 'connecting' | 'connected' | 'offline' | 'error';

export type ClientFrame =
  | { kind: 'ping'; ts?: number }
  | { kind: 'ack'; seq: number }
  | { kind: 'relay'; to: string; envelope: Envelope }
  | { kind: 'subscribe'; topics: string[] };

export interface HelloFrame {
  kind: 'hello';
  relay_version: number;
  protocol_version: number;
  device_id: string;
  role: RelayRole;
  server_time: number;
  seq: number;
  acked_seq: number;
  queue_depth: number;
}

export interface MessageFrame {
  kind: 'message';
  seq: number;
  envelope: Envelope;
  enqueued_at: number;
}

export interface PongFrame {
  kind: 'pong';
  ts: number;
}

export interface AckFrame {
  kind: 'ack';
  acked_seq: number;
  deleted: number;
}

export interface DeliveryFrame {
  kind: 'delivery';
  id: string;
  ok: boolean;
  seq?: number;
  error?: { code: ErrorCode; message: string };
}

export interface RelayErrorFrame {
  kind: 'error';
  code: ErrorCode;
  message: string;
  request_id?: string;
}

export type ServerFrame =
  HelloFrame | MessageFrame | PongFrame | AckFrame | DeliveryFrame | RelayErrorFrame;

/** Error shape surfaced by the client (fail-closed paths included). */
export interface RelayClientError {
  code: ErrorCode;
  message: string;
  retryable?: boolean;
  envelope_id?: string;
}

export interface RelaySeqGap {
  /** Next contiguous sequence number the client expected. */
  expected: number;
  /** First out-of-order sequence number received. */
  received: number;
  /** Highest sequence number buffered beyond the gap. */
  buffered: number;
}

export interface RelayResyncInfo {
  reason: 'gap';
  expected: number;
  received: number;
}

/** One decrypted protocol message delivered to the app layer. */
export interface RelayDecodedEvent {
  type: MessageType;
  payload: unknown;
  envelope: Envelope;
  /** Worker queue sequence number (monotonic per device). */
  seq: number;
  /** Sender device id (from the envelope when present). */
  from?: string;
}

export interface RelayE2EOptions {
  /** AES-256-GCM channel key derived from the X25519 pairing handshake. */
  channel: { key: CryptoKey; deviceId?: string };
  /** Default true: envelopes without ciphertext/nonce are rejected (fail closed). */
  requireEncrypted?: boolean;
}

export interface RelaySendResult {
  ok: boolean;
  /** `true` when the envelope was queued locally (offline / not open yet). */
  queued?: boolean;
  error?: RelayClientError;
}

export interface RelayTimers {
  setTimeout(handler: () => void, ms: number): ReturnType<typeof setTimeout>;
  clearTimeout(handle: ReturnType<typeof setTimeout>): void;
}

export interface RelayClientOptions {
  /** e.g. `wss://wintersrain.workers.dev/v1/ws` (or `wss://…/v1/ws?x=1`). */
  url: string;
  token: string;
  role?: RelayRole;
  onFrame?: (frame: ServerFrame) => void;
  onStatus?: (status: RelayStatus, detail?: string) => void;
  onHello?: (frame: HelloFrame) => void;
  /** Raw message-frame callback (unchanged legacy behaviour for AppState). */
  onMessage?: (frame: MessageFrame) => void;
  /** Decrypted protocol message callback (requires `e2e.channel`). */
  onEvent?: (event: RelayDecodedEvent) => void;
  onDelivery?: (frame: DeliveryFrame) => void;
  /** Fail-closed or queue/decryption errors; safe to surface to the user. */
  onError?: (error: RelayClientError) => void;
  /** Token was rejected/revoked: do not reconnect automatically. */
  onAuthError?: (error: RelayClientError) => void;
  onSeqGap?: (gap: RelaySeqGap) => void;
  onResync?: (info: RelayResyncInfo) => void;
  reconnect?: boolean;
  maxBackoffMs?: number;
  /** First reconnect delay (doubles per attempt; defaults to 1000 ms). */
  baseBackoffMs?: number;
  /** Extra random jitter in ms (defaults to 500; inject `random` for tests). */
  jitterMs?: number;
  /**
   * Consecutive auto-reconnect attempts before giving up (default 8). The
   * counter resets on every successful open and on `reconnectNow`. `0` disables
   * automatic retries while keeping the manual retry API.
   */
  maxReconnectAttempts?: number;
  /**
   * Drop a socket whose handshake never completes after this many ms and retry
   * (default 15000; `0` disables). Covers daemon restarts / half-open sockets
   * after a mobile suspend, where the browser may never fire `close`.
   */
  connectTimeoutMs?: number;
  /** Auto-reconnect attempts were exhausted (UI should offer a retry). */
  onReconnectExhausted?: () => void;
  /** Delay before a resync reconnect (defaults to 0 = immediately). */
  resyncDelayMs?: number;
  /** Offline outbound queue cap (default 100). */
  outboxLimit?: number;
  /** E2E channel key for `sendEvent`/`onEvent` (fail-closed when omitted). */
  e2e?: RelayE2EOptions;
  /** Injected WebSocket implementation (tests). */
  webSocketFactory?: (url: string) => WebSocket;
  /** Legacy injection kept for compatibility. */
  WebSocketImpl?: typeof WebSocket;
  /** Random source for reconnect jitter (tests inject `() => 0`). */
  random?: () => number;
  /** Timer injection for deterministic reconnect tests. */
  scheduler?: RelayTimers;
  /** Seconds clock injection (tests). */
  clock?: () => number;
}

const AUTH_CLOSE_CODES: ReadonlySet<number> = new Set([4401, 4403]);
const DEFAULT_TOPICS = ['approval', 'event', 'session', 'progress', 'chat'];
/**
 * How long an errored-but-still-`OPEN` socket may stay silent before the client
 * forces a clean reconnect. Browsers do not always fire `close` after a mobile
 * suspend / network switch, and the Worker socket has no server heartbeat.
 */
const ERROR_RECOVERY_MS = 5_000;

interface OutboxEntry {
  to: string;
  envelope: Envelope;
  queued_at: number;
}

const globalTimers: RelayTimers = {
  setTimeout: (handler, ms) => setTimeout(handler, ms),
  clearTimeout: (handle) => clearTimeout(handle),
};

export class RelayClient {
  private readonly options: RelayClientOptions;
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Handshake watchdog (a socket that never opens must not stay stuck). */
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private closedByUser = false;
  private authFailed = false;
  private everOpened = false;
  private currentStatus: RelayStatus = 'idle';
  private seen = new Set<string>();
  private readonly gapBuffer = new Map<number, MessageFrame>();
  private outbox: OutboxEntry[] = [];
  private readonly outboxIds = new Set<string>();
  private expectedSeq: number | null = null;
  private highestSeq = 0;
  private acknowledgedSeq = 0;
  private resyncing = false;
  private resyncInfo: RelayResyncInfo | null = null;
  /** Socket generation: stale async frames from a dropped socket are ignored. */
  private generation = 0;
  /** Serializes async E2E decoding so events stay in seq order. */
  private processing: Promise<void> = Promise.resolve();
  private readonly timers: RelayTimers;
  private readonly random: () => number;
  private readonly clock: () => number;

  constructor(options: RelayClientOptions) {
    this.options = options;
    this.timers = options.scheduler ?? globalTimers;
    this.random = options.random ?? Math.random;
    this.clock = options.clock ?? nowSeconds;
  }

  get status(): RelayStatus {
    return this.currentStatus;
  }

  /** Envelopes waiting locally for a connection (offline outbox). */
  get outboxSize(): number {
    return this.outbox.length;
  }

  /** Highest contiguous message sequence processed. */
  get lastSeq(): number {
    return this.expectedSeq === null ? this.highestSeq : this.expectedSeq - 1;
  }

  /** Highest ack sent to / reported by the Worker queue. */
  get ackedSeq(): number {
    return this.acknowledgedSeq;
  }

  /** Consecutive failed reconnect attempts since the last successful open. */
  get reconnectAttempts(): number {
    return this.attempt;
  }

  /** The token was rejected/revoked: automatic retries are suppressed. */
  get authFailedFatal(): boolean {
    return this.authFailed;
  }

  /** Policy derived from the options (single source for delay/cap). */
  private reconnectPolicy(): ReconnectPolicy {
    return {
      baseMs: this.options.baseBackoffMs ?? DEFAULT_RECONNECT_POLICY.baseMs,
      maxMs: this.options.maxBackoffMs ?? DEFAULT_RECONNECT_POLICY.maxMs,
      jitterMs: this.options.jitterMs ?? DEFAULT_RECONNECT_POLICY.jitterMs,
      maxAttempts: this.options.maxReconnectAttempts ?? DEFAULT_RECONNECT_POLICY.maxAttempts,
    };
  }

  connect(): void {
    this.closedByUser = false;
    this.authFailed = false;
    if (this.socket) return;
    const impl = this.resolveWebSocket();
    if (!impl) {
      this.setStatus('error', '当前环境不支持 WebSocket');
      return;
    }
    this.setStatus('connecting');
    try {
      this.socket = impl(this.buildUrl());
    } catch (error) {
      this.socket = null;
      this.setStatus('error', error instanceof Error ? error.message : 'WebSocket 创建失败');
      this.scheduleReconnect();
      return;
    }
    this.everOpened = false;
    const generation = this.generation + 1;
    this.generation = generation;
    const socket = this.socket;
    this.armSocketWatchdog(
      generation,
      this.options.connectTimeoutMs ?? 15_000,
      '中继握手超时，准备重连',
    );
    socket.onopen = () => {
      if (generation !== this.generation) return;
      this.clearConnectWatchdog();
      // A successful connection always resets the bounded-attempt counter.
      this.attempt = 0;
      this.everOpened = true;
      this.setStatus('connected');
      this.send({ kind: 'subscribe', topics: DEFAULT_TOPICS });
      this.flushOutbox();
    };
    socket.onmessage = (event: MessageEvent) => {
      if (generation !== this.generation) return;
      if (this.currentStatus === 'error' && this.socket?.readyState === 1) {
        // Frames are flowing again: the error was transient. Cancel the forced
        // reconnect and restore the usable-connection state.
        this.clearConnectWatchdog();
        this.setStatus('connected');
      }
      this.handleRaw(typeof event.data === 'string' ? event.data : '', generation);
    };
    socket.onclose = (event: CloseEvent) => {
      if (generation !== this.generation) return;
      this.clearConnectWatchdog();
      this.socket = null;
      this.everOpened = false;
      if (this.closedByUser) {
        this.setStatus('idle');
        return;
      }
      if (this.authFailed) return;
      const auth = classifyAuthClose(event?.code ?? 1006, event?.reason ?? '');
      if (auth) {
        this.failAuth(auth);
        return;
      }
      this.setStatus('offline', '中继连接已断开');
      this.scheduleReconnect();
    };
    socket.onerror = () => {
      if (generation !== this.generation) return;
      // A close event normally follows; status is set here for the UI.
      if (this.currentStatus !== 'error') this.setStatus('error', '连接错误');
      if (this.socket?.readyState === 1) {
        // The socket still claims to be open: give `close` a moment, then force
        // a clean reconnect if it never arrives (half-open mobile sockets).
        this.armSocketWatchdog(generation, ERROR_RECOVERY_MS, '中继连接异常，准备重连');
        return;
      }
      // Handshake failure / dead socket: retry with the bounded backoff and
      // consume the (now redundant) handshake watchdog.
      this.scheduleReconnect();
    };
  }

  /**
   * Reconnect immediately (browser `online` / `visibilitychange` / user retry).
   *
   * Never creates a second socket: a handshake already in flight is left to
   * finish (its watchdog covers a hang), and an open+healthy socket is kept
   * unless `force` is set. `resetAttempts` restores the bounded-attempt budget
   * after a network change or an exhausted backoff cycle.
   */
  reconnectNow(options: { resetAttempts?: boolean; force?: boolean } = {}): void {
    if (this.closedByUser || this.options.reconnect === false) return;
    // A rejected/revoked token stays fail-closed unless the caller explicitly
    // opts into a new budget (user retry / re-pair), matching the auth policy.
    if (this.authFailed && options.resetAttempts !== true) return;
    if (options.resetAttempts) {
      this.attempt = 0;
      this.authFailed = false;
    }
    this.cancelReconnect();
    const state = this.socket?.readyState;
    if (state === 0) return; // handshake in flight — no duplicate socket
    if (state === 1 && options.force !== true) return; // healthy connection
    if (this.socket) this.dropSocket();
    this.connect();
  }

  close(): void {
    this.closedByUser = true;
    this.generation += 1;
    this.cancelReconnect();
    this.clearConnectWatchdog();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        /* already closing */
      }
    }
    this.setStatus('idle');
  }

  /** Close the socket and release client-side buffers (used on teardown). */
  dispose(): void {
    this.close();
    this.seen.clear();
    this.gapBuffer.clear();
  }

  /** Raw client frame send; returns false when the socket is not open. */
  send(frame: ClientFrame): boolean {
    if (!this.socket || this.socket.readyState !== 1) return false;
    try {
      this.socket.send(JSON.stringify(frame));
      return true;
    } catch {
      return false;
    }
  }

  ack(seq: number): void {
    if (!Number.isInteger(seq) || seq < 0) return;
    if (this.send({ kind: 'ack', seq })) {
      this.acknowledgedSeq = Math.max(this.acknowledgedSeq, seq);
    }
  }

  /**
   * Relay an already-sealed envelope. When the socket is not open the envelope
   * is queued locally (bounded, deduplicated by envelope id) and replayed after
   * the next connection opens. Returns `false` only when the envelope is invalid.
   */
  relay(to: string, envelope: Envelope): boolean {
    return this.deliverOrQueue(to, envelope).ok;
  }

  /** Queue-or-send a raw envelope with a structured result (no E2E sealing). */
  sendEnvelope(to: string, envelope: Envelope): RelaySendResult {
    return this.deliverOrQueue(to, envelope);
  }

  /**
   * Seal `payload` with the E2E channel key and queue/send it.
   * Fails closed (`AUTH_REQUIRED`) when no channel key was configured.
   */
  async sendEvent(to: string, type: MessageType, payload: unknown): Promise<RelaySendResult> {
    const channel = this.options.e2e?.channel;
    if (!channel) {
      return {
        ok: false,
        error: { code: 'AUTH_REQUIRED', message: 'E2E 通道密钥未配置（需配对后派生共享密钥）' },
      };
    }
    try {
      const envelope = await encryptEnvelope({
        type,
        payload,
        key: channel.key,
        deviceId: channel.deviceId ?? this.options.e2e?.channel.deviceId,
      });
      return this.deliverOrQueue(to, envelope);
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'INTERNAL',
          message: `E2E 加密失败：${error instanceof Error ? error.message : '未知错误'}`,
        },
      };
    }
  }

  /**
   * Force a resync: drop the current socket and reconnect so the Worker replays
   * every unacknowledged queue entry. Safe to call at any time.
   */
  requestResync(reason: RelayResyncInfo['reason'] = 'gap'): void {
    const info: RelayResyncInfo = this.resyncInfo ?? {
      reason,
      expected: this.expectedSeq ?? 0,
      received: 0,
    };
    if (this.resyncing) return;
    this.resyncing = true;
    this.generation += 1;
    this.resyncInfo = info;
    this.options.onResync?.(info);
    if (!this.closedByUser && !this.authFailed) this.setStatus('connecting', '中继 resync 中');
    this.clearConnectWatchdog();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        /* already closing */
      }
    }
    this.cancelReconnect();
    if (this.options.reconnect === false) {
      this.resyncing = false;
      this.emitError({
        code: 'CONFLICT',
        message: '检测到 seq 缺口，但 reconnect 已关闭，无法 resync',
        retryable: false,
      });
      return;
    }
    this.scheduleReconnect(info.reason === 'gap' ? (this.options.resyncDelayMs ?? 0) : undefined);
  }

  /** Drop all locally queued outbound envelopes (e.g. after unpairing). */
  clearOutbox(): void {
    this.outbox = [];
    this.outboxIds.clear();
  }

  // -- WebSocket plumbing ---------------------------------------------------

  private resolveWebSocket(): ((url: string) => WebSocket) | null {
    const factory = this.options.webSocketFactory;
    if (factory) return factory;
    const Impl = this.options.WebSocketImpl;
    if (Impl) return (url) => new Impl(url);
    if (typeof WebSocket !== 'undefined') return (url) => new WebSocket(url);
    return null;
  }

  private buildUrl(): string {
    const separator = this.options.url.includes('?') ? '&' : '?';
    return `${this.options.url}${separator}token=${encodeURIComponent(this.options.token)}&role=${
      this.options.role ?? 'phone'
    }`;
  }

  private handleRaw(raw: string, generation: number): void {
    if (!raw) return;
    let frame: ServerFrame;
    try {
      frame = JSON.parse(raw) as ServerFrame;
    } catch {
      return;
    }
    if (typeof frame !== 'object' || frame === null || typeof frame.kind !== 'string') return;
    this.options.onFrame?.(frame);
    switch (frame.kind) {
      case 'hello':
        this.handleHello(frame);
        break;
      case 'message':
        if (isMessageFrame(frame)) this.enqueueProcessing(frame, generation);
        break;
      case 'delivery':
        this.options.onDelivery?.(frame);
        break;
      case 'error':
        this.handleErrorFrame(frame);
        break;
      default:
        // pong / ack: no client-side state beyond the raw onFrame callback.
        break;
    }
  }

  private handleHello(frame: HelloFrame): void {
    const acked = Number.isInteger(frame.acked_seq) ? frame.acked_seq : 0;
    this.acknowledgedSeq = Math.max(this.acknowledgedSeq, acked);
    this.expectedSeq = acked + 1;
    this.highestSeq = Math.max(this.highestSeq, acked);
    this.gapBuffer.clear();
    if (this.resyncing) {
      this.resyncing = false;
      this.resyncInfo = null;
    }
    this.options.onHello?.(frame);
  }

  private handleErrorFrame(frame: RelayErrorFrame): void {
    if (frame.code === 'AUTH_REQUIRED' || frame.code === 'AUTH_INVALID') {
      this.failAuth({ code: frame.code, message: frame.message, retryable: false });
      return;
    }
    // Preserve the original behaviour: any server error frame is a status error.
    this.setStatus('error', frame.message);
  }

  /** Token rejected/revoked: fail closed, close the socket, never auto-retry. */
  private failAuth(error: RelayClientError): void {
    this.authFailed = true;
    this.everOpened = false;
    this.generation += 1;
    this.cancelReconnect();
    this.clearConnectWatchdog();
    this.setStatus('error', `鉴权失败：${error.message}`);
    this.options.onAuthError?.(error);
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        /* already closing */
      }
    }
  }

  private enqueueProcessing(frame: MessageFrame, generation: number): void {
    this.processing = this.processing
      .then(() => {
        if (generation !== this.generation) return;
        return this.handleMessage(frame);
      })
      .catch((error: unknown) => {
        this.emitError({
          code: 'INTERNAL',
          message: `relay 消息处理失败：${error instanceof Error ? error.message : '未知错误'}`,
          retryable: false,
          ...(frame.envelope?.id ? { envelope_id: frame.envelope.id } : {}),
        });
      });
  }

  private async handleMessage(frame: MessageFrame): Promise<void> {
    const seq = frame.seq;
    if (typeof seq !== 'number' || !Number.isInteger(seq) || seq < 0) return;
    const envelope = frame.envelope;
    if (!isEnvelopeLike(envelope)) return;

    // Dedupe by envelope id: the Worker may replay after a reconnect/ack race.
    if (envelope.id && this.seen.has(envelope.id)) {
      this.ack(seq);
      return;
    }
    if (this.expectedSeq === null) this.expectedSeq = seq;
    const expected = this.expectedSeq;
    if (seq < expected) {
      // Old/duplicate sequence: ack is idempotent server-side.
      this.ack(seq);
      return;
    }
    if (seq > expected) {
      this.bufferGapFrame(frame);
      this.signalGap(expected, seq);
      return;
    }
    await this.acceptMessage(frame);
    this.expectedSeq = expected + 1;
    await this.drainGapBuffer();
  }

  private async acceptMessage(frame: MessageFrame): Promise<void> {
    const envelope = frame.envelope;
    if (envelope.id) {
      this.seen.add(envelope.id);
      if (this.seen.size > 2000) {
        this.seen = new Set([...this.seen].slice(-500));
      }
    }
    const decoded = await this.decode(envelope);
    if (decoded.kind === 'rejected') {
      // Fail closed: no raw/plaintext dispatch for a configured E2E channel.
      this.ack(frame.seq);
      return;
    }
    this.options.onMessage?.(frame);
    if (decoded.kind === 'ok') {
      this.options.onEvent?.({
        type: envelope.type,
        payload: decoded.payload,
        envelope,
        seq: frame.seq,
        ...(envelope.device_id ? { from: envelope.device_id } : {}),
      });
    }
    this.ack(frame.seq);
    this.highestSeq = Math.max(this.highestSeq, frame.seq);
  }

  private async decode(
    envelope: Envelope,
  ): Promise<{ kind: 'ok'; payload: unknown } | { kind: 'raw' } | { kind: 'rejected' }> {
    const e2e = this.options.e2e;
    if (!e2e) return { kind: 'raw' };
    const encrypted = Boolean(envelope.ciphertext && envelope.nonce);
    if (encrypted) {
      try {
        const payload = await decryptEnvelope(e2e.channel.key, envelope);
        return { kind: 'ok', payload };
      } catch (error) {
        this.emitError({
          code: 'AUTH_INVALID',
          message: `E2E 信封解密失败：${error instanceof Error ? error.message : '未知错误'}`,
          retryable: false,
          ...(envelope.id ? { envelope_id: envelope.id } : {}),
        });
        return { kind: 'rejected' };
      }
    }
    if (e2e.requireEncrypted === false) {
      return envelope.payload !== undefined
        ? { kind: 'ok', payload: envelope.payload }
        : { kind: 'raw' };
    }
    this.emitError({
      code: 'AUTH_INVALID',
      message: '拒绝未加密的信封（E2E 通道要求密文，fail-closed）',
      retryable: false,
      ...(envelope.id ? { envelope_id: envelope.id } : {}),
    });
    return { kind: 'rejected' };
  }

  private bufferGapFrame(frame: MessageFrame): void {
    if (!this.gapBuffer.has(frame.seq)) this.gapBuffer.set(frame.seq, frame);
    if (this.gapBuffer.size > 256) {
      // Bound memory; the server can replay unacked entries after resync.
      const first = this.gapBuffer.keys().next();
      if (!first.done) this.gapBuffer.delete(first.value);
    }
  }

  private signalGap(expected: number, received: number): void {
    this.options.onSeqGap?.({ expected, received, buffered: this.gapBuffer.size });
    if (this.resyncing) return;
    this.resyncInfo = { reason: 'gap', expected, received };
    this.requestResync('gap');
  }

  private async drainGapBuffer(): Promise<void> {
    for (;;) {
      const expected = this.expectedSeq;
      if (expected === null) return;
      const frame = this.gapBuffer.get(expected);
      if (!frame) return;
      this.gapBuffer.delete(expected);
      await this.acceptMessage(frame);
      this.expectedSeq = expected + 1;
    }
  }

  // -- Outbound queue -------------------------------------------------------

  private deliverOrQueue(to: string, envelope: Envelope): RelaySendResult {
    if (!to || !isEnvelopeLike(envelope)) {
      return {
        ok: false,
        error: { code: 'INVALID_REQUEST', message: 'relay 需要有效的目标设备与信封' },
      };
    }
    if (envelope.id && this.outboxIds.has(envelope.id)) {
      return { ok: true, queued: true };
    }
    const frame: ClientFrame = { kind: 'relay', to, envelope };
    if (this.send(frame)) return { ok: true, queued: false };
    this.enqueueOutbox(to, envelope);
    return { ok: true, queued: true };
  }

  private enqueueOutbox(to: string, envelope: Envelope): void {
    const limit = this.options.outboxLimit ?? 100;
    if (envelope.id && this.outboxIds.has(envelope.id)) return;
    if (this.outbox.length >= limit) {
      const dropped = this.outbox.shift();
      if (dropped?.envelope.id) this.outboxIds.delete(dropped.envelope.id);
      this.emitError({
        code: 'RATE_LIMIT',
        message: `relay 离线队列已满（${limit}），已丢弃最旧信封`,
        retryable: true,
        ...(dropped?.envelope.id ? { envelope_id: dropped.envelope.id } : {}),
      });
    }
    this.outbox.push({ to, envelope, queued_at: this.clock() });
    if (envelope.id) this.outboxIds.add(envelope.id);
  }

  private flushOutbox(): void {
    while (this.outbox.length > 0) {
      const entry = this.outbox[0];
      if (!entry) return;
      if (!this.send({ kind: 'relay', to: entry.to, envelope: entry.envelope })) return;
      this.outbox.shift();
      if (entry.envelope.id) this.outboxIds.delete(entry.envelope.id);
    }
  }

  // -- Reconnect ------------------------------------------------------------

  private scheduleReconnect(overrideDelay?: number): void {
    if (this.options.reconnect === false) return;
    if (this.closedByUser || this.authFailed) return;
    if (this.reconnectTimer !== null) return;
    // A scheduled retry supersedes any per-socket watchdog.
    this.clearConnectWatchdog();
    let delay: number;
    if (overrideDelay !== undefined) {
      // Resync/replay is not part of the bounded backoff cycle.
      delay = Math.max(0, overrideDelay);
    } else {
      const policy = this.reconnectPolicy();
      if (!canReconnect(this.attempt, policy)) {
        this.setStatus(
          'error',
          `中继重连已达上限（${policy.maxAttempts} 次），请点按重试或切换网络`,
        );
        this.options.onReconnectExhausted?.();
        return;
      }
      delay = reconnectDelay(this.attempt, policy, this.random);
      this.attempt += 1;
    }
    this.reconnectTimer = this.timers.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closedByUser || this.authFailed) return;
      if (this.socket) {
        // Healthy connection: nothing to do. Otherwise (error frame without a
        // close / half-open socket) drop it so the retry can really re-handshake.
        if (this.socket.readyState === 1 && this.currentStatus === 'connected') return;
        this.dropSocket();
      }
      this.connect();
    }, delay);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer === null) return;
    this.timers.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  /**
   * Detach and close the current socket without touching user intent. A stale
   * socket must never emit callbacks after a forced reconnect.
   */
  private dropSocket(): void {
    this.cancelReconnect();
    this.clearConnectWatchdog();
    const socket = this.socket;
    this.socket = null;
    this.everOpened = false;
    this.generation += 1;
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch {
      /* already closing */
    }
  }

  /**
   * A socket that never completes its handshake or stops making progress
   * (daemon restart, suspended tab, network switch) would otherwise leave the
   * client “connecting/error” forever. Arm a watchdog per socket generation and
   * force one clean reconnect when it expires.
   */
  private armSocketWatchdog(generation: number, timeoutMs: number, detail: string): void {
    this.clearConnectWatchdog();
    if (!(timeoutMs > 0)) return;
    this.connectTimer = this.timers.setTimeout(() => {
      this.connectTimer = null;
      if (generation !== this.generation) return;
      if (this.closedByUser || this.authFailed) return;
      if (this.currentStatus === 'connected') return;
      this.dropSocket();
      this.setStatus('offline', detail);
      this.scheduleReconnect();
    }, timeoutMs);
  }

  private clearConnectWatchdog(): void {
    if (this.connectTimer === null) return;
    this.timers.clearTimeout(this.connectTimer);
    this.connectTimer = null;
  }

  // -- Helpers --------------------------------------------------------------

  private setStatus(status: RelayStatus, detail?: string): void {
    this.currentStatus = status;
    this.options.onStatus?.(status, detail);
  }

  private emitError(error: RelayClientError): void {
    this.options.onError?.(error);
  }
}

function classifyAuthClose(code: number, reason: string): RelayClientError | null {
  if (!AUTH_CLOSE_CODES.has(code) && !/AUTH_(?:REQUIRED|INVALID)/u.test(reason)) return null;
  const codeValue: ErrorCode = code === 4401 ? 'AUTH_REQUIRED' : 'AUTH_INVALID';
  return {
    code: codeValue,
    message: reason.trim() || (code === 4401 ? '缺少或过期的设备令牌' : '设备令牌无效或已吊销'),
    retryable: false,
  };
}

function isEnvelopeLike(value: unknown): value is Envelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Envelope).id === 'string' &&
    typeof (value as Envelope).type === 'string' &&
    typeof (value as Envelope).ts === 'number'
  );
}

function isMessageFrame(frame: ServerFrame): frame is MessageFrame {
  const candidate = frame as MessageFrame;
  return (
    candidate.kind === 'message' &&
    typeof candidate.seq === 'number' &&
    Number.isInteger(candidate.seq) &&
    candidate.seq >= 0 &&
    typeof candidate.envelope === 'object' &&
    candidate.envelope !== null
  );
}

/** Build the Worker WebSocket URL from the HTTP base URL. */
export function relayUrlFromWorkerUrl(workerUrl: string): string {
  const trimmed = workerUrl.trim().replace(/\/+$/u, '');
  if (!trimmed) return '';
  return `${trimmed.replace(/^http/u, 'ws')}/v1/ws`;
}

/** Used by the UI: "connected at HH:MM" style timestamps. */
export function relayStatusLabel(status: RelayStatus): string {
  switch (status) {
    case 'idle':
      return '未连接';
    case 'connecting':
      return '连接中';
    case 'connected':
      return '中继已连接';
    case 'offline':
      return '中继离线';
    case 'error':
      return '连接异常';
  }
}

export { nowSeconds };
